/**
 * 파일명: contactUs.js
 * 작성자: 김준형
 * 작성일자: 26.08.10
 * 설명: About Us - Contact Us 문의 폼
 */
import React, { useCallback, useState } from "react";
import useAboutBg from "components/about/aboutAsset";
import TermsModal from "components/common/termsModal";
import PRIVACY_CONSENT from "components/common/docs/privacyConsentDoc";
import style from "css/about.module.css";

const SUCCESS_MESSAGE = "문의가 접수되었습니다. 담당자가 빠르게 연락드리겠습니다.";
const FAIL_MESSAGE = "전송에 실패했습니다. 잠시 후 다시 시도해 주세요.";

const PHONE_PREFIXES = ["010", "011", "016", "017", "018", "019"];

const INIT_FORM = {
  name: "",
  company: "",
  phone1: "010",
  phone2: "",
  phone3: "",
  email: "",
  message: "",
  agree: false,
  website: "",
};

function CaretIcon() {
  return (
    <svg
      className={style.ab_cu_caret}
      viewBox="0 0 16 10"
      aria-hidden="true"
    >
      <path
        d="M1 1.5 8 8.5l7-7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
      />
    </svg>
  );
}

function ContactUs() {
  const [form, setForm] = useState(INIT_FORM);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sending, setSending] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const bg = useAboutBg();

  const closeConsent = useCallback(() => setConsentOpen(false), []);

  const agreeConsent = useCallback(() => {
    setForm((prev) => ({ ...prev, agree: true }));
    setError("");
    setSuccess("");
    setConsentOpen(false);
  }, []);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const setField = (key) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;

    setForm((prev) => ({ ...prev, [key]: value }));
    clearMessages();
  };

  const setDigits = (key, max) => (event) => {
    const value = event.target.value.replace(/[^0-9]/g, "").slice(0, max);
    setForm((prev) => ({ ...prev, [key]: value }));
    clearMessages();
  };

  const validate = () => {
    if (!form.name.trim()) return "성함을 입력해 주세요.";
    if (!form.company.trim()) return "소속(회사)을 입력해 주세요.";
    if (!form.phone2 || !form.phone3) return "연락처를 입력해 주세요.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return "이메일주소 전체를 입력해 주세요.";
    if (!form.agree) return "개인정보 수집 및 이용에 동의해 주세요.";
    return "";
  };

  const submit = async (event) => {
    event.preventDefault();

    if (sending) return;

    const message = validate();
    if (message) {
      setError(message);
      setSuccess("");
      return;
    }

    clearMessages();
    setSending(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/contact`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        },
      );

      const data = await res.json();

      if (res.ok && data.ok) {
        setForm(INIT_FORM);
        setSuccess(SUCCESS_MESSAGE);
        return;
      }

      setError(data.error || FAIL_MESSAGE);
    } catch (err) {
      console.error("[contact] 문의 전송 실패", err);
      setError(FAIL_MESSAGE);
    } finally {
      setSending(false);
    }
  };

  const renderLabel = (text, required) => (
    <span className={style.ab_cu_label}>
      {text}
      {required && <em className={style.ab_cu_required}> *</em>}
    </span>
  );

  return (
    <section
      id="contact"
      className={style.ab_cu}
      style={bg("contact")}
    >
      <div className={style.ab_cu_inner}>
        <div className={style.ab_cu_left}>
          <span className={style.ab_cu_badge}>Contact Us</span>
          <h2 className={style.ab_cu_title}>
            Smart WorkPlace &amp; Telehealth 등
            <br />
            최고의 기술력과 레퍼런스를 바탕으로
            <br />
            최적의 솔루션을 제공합니다.
          </h2>
          <p className={style.ab_cu_desc}>
            문의를 남겨주시면 담당 전문가가 최대한 빠른 시일내에
            <br />
            직접 연락드려 상세한 가이드를 제공합니다.
          </p>
        </div>

        <form
          className={style.ab_cu_form}
          onSubmit={submit}
          noValidate
        >
          <div className={style.ab_cu_row}>
            {renderLabel("성함", true)}
            <input
              className={style.ab_cu_input}
              type="text"
              value={form.name}
              onChange={setField("name")}
              placeholder="성함을 입력해 주세요."
            />
          </div>

          <div className={style.ab_cu_row}>
            {renderLabel("소속(회사)", true)}
            <input
              className={style.ab_cu_input}
              type="text"
              value={form.company}
              onChange={setField("company")}
              placeholder="회사명 또는 기관명을 입력해 주세요."
            />
          </div>

          <div className={style.ab_cu_row}>
            {renderLabel("연락처", true)}
            <div className={style.ab_cu_phone}>
              <span className={style.ab_cu_select_wrap}>
                <select
                  className={`${style.ab_cu_input} ${style.ab_cu_select}`}
                  value={form.phone1}
                  onChange={setField("phone1")}
                  aria-label="통신사 앞자리"
                >
                  {PHONE_PREFIXES.map((prefix) => (
                    <option
                      key={prefix}
                      value={prefix}
                    >
                      {prefix}
                    </option>
                  ))}
                </select>
                <CaretIcon />
              </span>
              <span className={style.ab_cu_hyphen}>-</span>
              <input
                className={`${style.ab_cu_input} ${style.ab_cu_input_phone}`}
                type="tel"
                inputMode="numeric"
                value={form.phone2}
                onChange={setDigits("phone2", 4)}
                aria-label="연락처 가운데 자리"
              />
              <span className={style.ab_cu_hyphen}>-</span>
              <input
                className={`${style.ab_cu_input} ${style.ab_cu_input_phone}`}
                type="tel"
                inputMode="numeric"
                value={form.phone3}
                onChange={setDigits("phone3", 4)}
                aria-label="연락처 끝 자리"
              />
            </div>
          </div>

          <div className={style.ab_cu_row}>
            {renderLabel("이메일", true)}
            <input
              className={style.ab_cu_input}
              type="email"
              value={form.email}
              onChange={setField("email")}
              placeholder="이메일주소 전체를 입력해 주세요."
            />
          </div>

          <div className={`${style.ab_cu_row} ${style.ab_cu_row_message}`}>
            {renderLabel("문의내용", false)}
            <div className={style.ab_cu_message_col}>
              <textarea
                className={`${style.ab_cu_input} ${style.ab_cu_textarea}`}
                value={form.message}
                onChange={setField("message")}
                placeholder="문의내용을 자유롭게 작성해 주세요."
              />
              <div className={style.ab_cu_agree}>
                <input
                  className={style.ab_cu_checkbox}
                  type="checkbox"
                  checked={form.agree}
                  onChange={setField("agree")}
                  aria-label="개인정보 수집 및 이용 동의"
                />
                <button
                  type="button"
                  className={style.ab_cu_agree_link}
                  onClick={() => setConsentOpen(true)}
                >
                  개인정보 수집 및 이용 동의
                </button>
                <em className={style.ab_cu_required}> *</em>
              </div>

              {(error || success) && (
                <p
                  className={`${style.ab_cu_msg} ${
                    error ? style.ab_cu_error : style.ab_cu_success
                  }`}
                  role={error ? "alert" : "status"}
                >
                  <span
                    className={style.ab_cu_msg_icon}
                    aria-hidden="true"
                  >
                    {error ? "!" : "✓"}
                  </span>
                  {error || success}
                </p>
              )}
            </div>
          </div>

          <input
            className={style.ab_cu_hp}
            type="text"
            name="website"
            value={form.website}
            onChange={setField("website")}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />

          <div className={style.ab_cu_submit_row}>
            <button
              type="submit"
              className={style.ab_cu_submit}
              disabled={sending}
            >
              {sending ? "전송 중..." : "제출하기"}
            </button>
          </div>
        </form>
      </div>

      <TermsModal
        open={consentOpen}
        doc={PRIVACY_CONSENT}
        onClose={closeConsent}
        onAgree={agreeConsent}
      />
    </section>
  );
}

export default ContactUs;
